import type { EntitlementStatus } from '@secretario/shared';
import { Link } from 'wouter';
import { canBuyCreditsHere } from '../../lib/plan.js';

/** Uma barra da franquia: quanto já foi de quanto cabe. */
type Bar = { key: string; label: string; used: number; limit: number; value: string };

/**
 * O que a pessoa já usou do plano, mês a mês e no dia: pedidos, minutos ao
 * vivo, documentos e créditos. Os termos (4.5) prometem isto na tela do plano;
 * aqui fica à vista sem precisar abrir a loja. Só mostra o que o plano tem.
 */
export function usageBars(status: EntitlementStatus): Bar[] {
  const { ai, voice, documents } = status.features;
  const bars: Bar[] = [];
  if (ai.monthlyLimit) {
    bars.push({
      key: 'month',
      label: 'Pedidos no mês',
      used: ai.usedMonth,
      limit: ai.monthlyLimit,
      value: `${fmt(ai.usedMonth)} de ${fmt(ai.monthlyLimit)}`,
    });
  }
  if (ai.dailyLimit) {
    bars.push({
      key: 'day',
      label: 'Pedidos hoje',
      used: ai.usedToday,
      limit: ai.dailyLimit,
      value: `${fmt(ai.usedToday)} de ${fmt(ai.dailyLimit)}`,
    });
  }
  if (voice.enabled && voice.limitMinutes > 0) {
    const used = Math.min(voice.limitMinutes, Math.round(voice.usedSeconds / 60));
    bars.push({
      key: 'voice',
      label: voice.sample ? 'Conversa ao vivo (amostra)' : 'Conversa ao vivo',
      used,
      limit: voice.limitMinutes,
      value: `${used} de ${voice.limitMinutes} min`,
    });
  }
  if (documents.monthlyLimit) {
    bars.push({
      key: 'docs',
      label: documents.sample ? 'Documentos prontos (amostra)' : 'Documentos prontos',
      used: documents.used,
      limit: documents.monthlyLimit,
      value: `${documents.used} de ${documents.monthlyLimit}`,
    });
  }
  return bars;
}

/** Aviso único: a primeira franquia do mês que passou de 80%. */
export function usageWarning(bars: Bar[]): string | null {
  const hot = bars.find((bar) => bar.key !== 'day' && bar.used / bar.limit >= 0.8);
  if (!hot) return null;
  if (hot.used >= hot.limit) {
    return hot.key === 'month'
      ? 'Os pedidos do mês acabaram. Enquanto não renova, entram os créditos.'
      : `${hot.label}: chegou ao fim este mês.`;
  }
  return hot.key === 'month'
    ? 'Você já usou mais de 80% dos pedidos do mês. Quando acabar, entram os créditos.'
    : `${hot.label}: já foi mais de 80% do mês.`;
}

export function UsageCard({ status }: { status: EntitlementStatus }) {
  const bars = usageBars(status);
  const credits = status.features.credits.balance;
  const renews = status.features.ai.monthResetAt ?? status.features.ai.resetAt;
  const warning = usageWarning(bars);
  return (
    <div className="ct-usage" aria-label="Seu uso">
      {bars.map((bar) => {
        const ratio = Math.min(1, bar.limit ? bar.used / bar.limit : 0);
        return (
          <div key={bar.key} className={`pgs-bar${ratio >= 0.8 ? ' high' : ''}`}>
            <span>{bar.label}</span>
            <b>{bar.value}</b>
            <i>
              <em style={{ transform: `scaleX(${ratio})` }} />
            </i>
          </div>
        );
      })}
      {bars.length === 0 && <p className="ct-note">Sem franquia para mostrar neste plano.</p>}
      {warning && <p className="ct-note">{warning}</p>}
      <div className="ct-usage-foot">
        <span>
          {credits > 0
            ? `${fmt(credits)} ${credits === 1 ? 'crédito' : 'créditos'} de reserva`
            : 'Sem créditos de reserva'}
          {renews && bars.some((bar) => bar.key !== 'day') ? ` · renova ${shortDate(renews)}` : ''}
        </span>
        {/* Só com a compra ligada aqui (loja no app, Stripe na web): sem ela, o link levava a
            um "em breve". */}
        {canBuyCreditsHere(status.features.credits.webSales) && (
          <Link href="/plano">{credits > 0 ? 'Mais créditos' : 'Comprar créditos'}</Link>
        )}
      </div>
    </div>
  );
}

function fmt(value: number) {
  return value.toLocaleString('pt-BR');
}

function shortDate(iso: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `em ${date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace('.', '')}`;
}
